import { BitkitAvatar, BitkitLink, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useEffect, useState } from 'react';

import defaultIcon from '@/assets/step-icon-default.svg';
import DataTable from '@/components/DataTable/DataTable';
import { DataTableHeader } from '@/components/DataTable/types/DataTable.type';
import useStepLib from '@/hooks/api/useStepLib';
import { CacheCandidateStep } from '@/models/BuildCacheInfo';
import { getStepsData, GetStepsDataReturn } from '@/utils/buildLog';
import { formatDuration } from '@/utils/commonFormats';

const tableHeaders: DataTableHeader[] = [
  {
    key: 'step',
    label: 'Step',
  },
  {
    key: 'duration',
    label: 'Duration',
    isNumeric: true,
  },
  {
    key: 'optimized_duration',
    label: 'Optimized duration*',
    isNumeric: true,
  },
];

type OptimizableGradleStepsProps = {
  steps: CacheCandidateStep[];
};

const OptimizableGradleSteps = ({ steps }: OptimizableGradleStepsProps) => {
  const { isMobile: isCompact } = useResponsive();

  const [stepsData, setStepsData] = useState<GetStepsDataReturn | null>(null);
  const { data, isLoading: isStepLibLoading } = useStepLib();

  useEffect(() => {
    if (data && !isStepLibLoading) {
      setStepsData(getStepsData(data));
    }
  }, [data, isStepLibLoading]);

  const tableRows = steps?.map((step) => {
    let stepIconURL;
    try {
      stepIconURL = stepsData?.stepIconURLs[step.id][stepsData?.latestStepVersions[step.id]] || defaultIcon;
    } catch {
      stepIconURL = defaultIcon;
    }

    return {
      fields: {
        step: (
          <Text textStyle="body/md/regular" color="text/body" display="flex" gap="8" alignItems="center">
            <BitkitAvatar name={step.title} src={stepIconURL} variant="image" />
            {step.title}
          </Text>
        ),
        duration: formatDuration(step.duration),
        optimized_duration: (
          <Text textStyle="body/md/regular" color="text/body">
            {formatDuration(step.optimizedDuration.low)} - {formatDuration(step.optimizedDuration.high)}
          </Text>
        ),
      },
    };
  });

  return (
    <Box display="flex" flexDirection="column" gap="16">
      <Box display="flex" flexDir="column" gap=".25rem" paddingBottom="16">
        <Text textStyle="heading/h3" color="text/primary">
          Optimizable Gradle Steps
        </Text>
        <Text textStyle="body/md/regular" color="text/secondary">
          The following Steps can be optimized with Build Cache.
        </Text>
      </Box>
      <DataTable
        dataTestId="optimizable-gradle-steps-table"
        headers={tableHeaders}
        isCompact={isCompact}
        rows={tableRows}
        tableProps={{
          isFixed: true,
          variant: 'borderless',
          width: '100%',
        }}
      />
      <Text textStyle="body/sm/regular" color="text/secondary" textAlign="right">
        *Optimized duration is estimated based on{' '}
        <BitkitLink
          colorVariant="purple"
          href="https://bitrise.io/blog/post/bitrise-build-cache-reduces-circleci-build-times"
          isExternal
        >
          benchmark data
        </BitkitLink>
        .
      </Text>
    </Box>
  );
};

export default OptimizableGradleSteps;
